/**
 * AdminAnalytics — DuckDB-powered analytics panel (admin only).
 * Rendered as a section inside the existing Admin dashboard (no routing change).
 * Uses inline SVG sparklines — zero new dependencies.
 */
import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  BarChart2,
  Award,
  Zap,
  RefreshCw,
  Calendar,
  Target,
  ArrowUpRight,
} from "lucide-react";
import { fetchAnalytics } from "../../api/admin";
import { Card } from "../../components/ui/card";

// ─── tiny helpers ────────────────────────────────────────────────────────────

function fmt(n) {
  if (n == null) return "–";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return String(n);
}

function GrowthBadge({ pct }) {
  if (pct == null)
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-stone-400 font-medium">
        <Minus size={11} /> n/a
      </span>
    );
  const pos = pct >= 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded-full ${
        pos
          ? "bg-emerald-50 text-emerald-600 border border-emerald-200"
          : "bg-red-50 text-red-600 border border-red-200"
      }`}
    >
      {pos ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
      {pos ? "+" : ""}
      {pct}%
    </span>
  );
}

// ─── sparkline SVG ───────────────────────────────────────────────────────────

function Sparkline({ data, color = "#6366f1", fill = false }) {
  if (!data || data.length < 2) return null;
  const W = 120,
    H = 36,
    pad = 2;
  const max = Math.max(...data, 1);
  const pts = data.map((v, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((v / max) * (H - pad * 2));
    return `${x},${y}`;
  });
  const polyline = pts.join(" ");
  const area =
    `${pad},${H - pad} ` + polyline + ` ${W - pad},${H - pad}`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-24 h-8 shrink-0">
      {fill && (
        <polygon
          points={area}
          fill={color}
          fillOpacity="0.12"
        />
      )}
      <polyline
        points={polyline}
        fill="none"
        stroke={color}
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ─── mini bar chart ──────────────────────────────────────────────────────────

function MiniBar({ data, labelKey, valueKey, color = "#6366f1" }) {
  if (!data?.length) return <p className="text-xs text-stone-400 italic">Aucune donnée</p>;
  const max = Math.max(...data.map((d) => d[valueKey]), 1);
  return (
    <div className="space-y-1.5">
      {data.map((d, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <span className="w-28 truncate text-stone-600 font-medium shrink-0">
            {d[labelKey] || `Client #${d.client_id}`}
          </span>
          <div className="flex-1 bg-stone-100 rounded-full h-2 overflow-hidden">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${(d[valueKey] / max) * 100}%`,
                background: color,
              }}
            />
          </div>
          <span className="w-10 text-right text-stone-500 font-mono shrink-0">
            {fmt(d[valueKey])}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── daily sparkline card ─────────────────────────────────────────────────────

function TrendCard({ title, data, metricKey, color, icon: Icon, subtitle }) {
  const values = (data || []).map((d) => d[metricKey] || 0);
  const total = values.reduce((s, v) => s + v, 0);
  return (
    <Card className="p-4 flex flex-col gap-2 bg-white border border-stone-200 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Icon size={14} className="text-stone-400 shrink-0" />
          <span className="text-xs font-bold uppercase tracking-wide text-stone-500">
            {title}
          </span>
        </div>
        <Sparkline data={values} color={color} fill />
      </div>
      <div className="text-2xl font-bold text-stone-800">{fmt(total)}</div>
      {subtitle && (
        <p className="text-[11px] text-stone-400">{subtitle}</p>
      )}
    </Card>
  );
}

// ─── client breakdown table ───────────────────────────────────────────────────

function ClientBreakdownTable({ rows }) {
  if (!rows?.length)
    return (
      <p className="text-sm text-stone-400 italic text-center py-6">
        Aucune donnée sur 30 jours
      </p>
    );
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-stone-100">
            {["Client", "Requêtes", "Tokens", "OCR", "Embeddings", "Moy. tok/req"].map(
              (h) => (
                <th
                  key={h}
                  className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-stone-400"
                >
                  {h}
                </th>
              )
            )}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={i}
              className="border-b border-stone-50 hover:bg-stone-50 transition-colors"
            >
              <td className="px-3 py-2.5 font-semibold text-stone-700 max-w-[140px] truncate">
                {r.name || `Client #${r.client_id}`}
              </td>
              <td className="px-3 py-2.5 font-mono text-stone-600">
                {(r.queries || 0).toLocaleString()}
              </td>
              <td className="px-3 py-2.5 font-mono text-stone-600">
                {fmt(r.tokens || 0)}
              </td>
              <td className="px-3 py-2.5 font-mono text-stone-600">
                {(r.ocr || 0).toLocaleString()}
              </td>
              <td className="px-3 py-2.5 font-mono text-stone-600">
                {fmt(r.embeddings || 0)}
              </td>
              <td className="px-3 py-2.5 font-mono text-brand-red font-semibold">
                {r.avg_tokens_per_query?.toLocaleString() ?? "–"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── busiest days ─────────────────────────────────────────────────────────────

function BusiestDays({ rows }) {
  if (!rows?.length) return null;
  const max = rows[0]?.queries || 1;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="flex items-center gap-3 text-xs">
          <span className="w-24 font-mono text-stone-500 shrink-0">{r.date}</span>
          <div className="flex-1 bg-stone-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-1.5 rounded-full bg-brand-red/70"
              style={{ width: `${(r.queries / max) * 100}%` }}
            />
          </div>
          <span className="w-12 text-right text-stone-600 font-semibold font-mono shrink-0">
            {r.queries.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── KPI pill ────────────────────────────────────────────────────────────────

function KpiPill({ label, value, sub, color = "brand" }) {
  const colors = {
    brand: "bg-brand-red/10 border-brand-red/20 text-brand-red",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
  };
  return (
    <div
      className={`flex flex-col gap-0.5 px-4 py-3 rounded-xl border ${colors[color] || colors.indigo}`}
    >
      <span className="text-[10px] font-bold uppercase tracking-wider opacity-60">
        {label}
      </span>
      <span className="text-xl font-bold leading-none">{value}</span>
      {sub && <span className="text-[11px] opacity-70">{sub}</span>}
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function AdminAnalytics() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchAnalytics();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Erreur de chargement analytics");
    } finally {
      setLoading(false);
    }
  }, []);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    load();
  }, [load]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const kpis = data?.platform_kpis;
  const trend30 = data?.daily_trend_30d || [];

  return (
    <div className="space-y-6">
      {/* ── header ── */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <BarChart2 size={20} className="text-brand-red shrink-0" />
          <h2 className="text-lg font-semibold text-stone-800">Analytics avancées</h2>
          <span className="text-xs text-stone-400 font-medium bg-stone-100 px-2 py-0.5 rounded-full">
            DuckDB · 30 jours
          </span>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 text-xs text-stone-500 hover:text-brand-red hover:bg-brand-red/5 px-3 py-1.5 rounded-lg border border-stone-200 transition-all font-medium disabled:opacity-40"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={`skeleton-${i}`} className="h-20 rounded-xl bg-stone-100 animate-pulse" />
          ))}
        </div>
      )}

      {data && (
        <>
          {/* ── KPI pills ── */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <KpiPill
              label="Requêtes 7j"
              value={fmt(kpis?.queries_7d)}
              sub={<GrowthBadge pct={kpis?.queries_growth_pct} />}
              color="indigo"
            />
            <KpiPill
              label="Tokens 7j"
              value={fmt(kpis?.tokens_7d)}
              sub={<GrowthBadge pct={kpis?.tokens_growth_pct} />}
              color="violet"
            />
            <KpiPill
              label="Jours actifs (30j)"
              value={kpis?.active_days_30d ?? "–"}
              sub="jours avec ≥1 requête"
              color="emerald"
            />
            <KpiPill
              label="Moy. req/jour actif"
              value={kpis?.avg_queries_active_day ?? "–"}
              sub="moyenne journalière"
              color="amber"
            />
            <KpiPill
              label="Record journalier"
              value={fmt(kpis?.peak_queries_day)}
              sub="requêtes en 1 jour"
              color="rose"
            />
          </div>

          {/* ── sparkline trend cards ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <TrendCard
              title="Requêtes / jour"
              data={trend30}
              metricKey="queries"
              color="#6366f1"
              icon={Target}
              subtitle="Total sur 30 jours"
            />
            <TrendCard
              title="Tokens / jour"
              data={trend30}
              metricKey="tokens"
              color="#8b5cf6"
              icon={Zap}
              subtitle="Tokens LLM consommés"
            />
            <TrendCard
              title="OCR / jour"
              data={trend30}
              metricKey="ocr"
              color="#10b981"
              icon={Calendar}
              subtitle="Pages OCR traitées"
            />
            <TrendCard
              title="Embeddings / jour"
              data={trend30}
              metricKey="embeddings"
              color="#f59e0b"
              icon={ArrowUpRight}
              subtitle="Vecteurs générés"
            />
          </div>

          {/* ── top clients ── */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-5 border border-stone-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Award size={16} className="text-brand-red" />
                <h3 className="text-sm font-semibold text-stone-700">
                  Top clients — Requêtes (30j)
                </h3>
              </div>
              <MiniBar
                data={data.top_clients_queries_30d || []}
                labelKey="name"
                valueKey="total"
                color="#6366f1"
              />
            </Card>
            <Card className="p-5 border border-stone-200 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Award size={16} className="text-violet-500" />
                <h3 className="text-sm font-semibold text-stone-700">
                  Top clients — Tokens (30j)
                </h3>
              </div>
              <MiniBar
                data={data.top_clients_tokens_30d || []}
                labelKey="name"
                valueKey="total"
                color="#8b5cf6"
              />
            </Card>
          </div>

          {/* ── client breakdown table ── */}
          <Card className="p-5 border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <BarChart2 size={16} className="text-stone-500" />
              <h3 className="text-sm font-semibold text-stone-700">
                Détail par client — 30 derniers jours
              </h3>
            </div>
            <ClientBreakdownTable rows={data.client_breakdown_30d} />
          </Card>

          {/* ── busiest days ── */}
          <Card className="p-5 border border-stone-200 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp size={16} className="text-rose-500" />
              <h3 className="text-sm font-semibold text-stone-700">
                Jours les plus actifs (tous temps)
              </h3>
            </div>
            <BusiestDays rows={data.busiest_days} />
          </Card>
        </>
      )}
    </div>
  );
}
